import {Route} from 'vue-router';
import store from '@/store';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';
import {hasTeacherPortal, portalMode, resolvePortalRoute, TEACHER_PORTAL_HOME} from '@/helpers/portalAccess.helper';
import {portalTabAllowed} from '@/components/teacherPortal/portalNav';

export const PARTNER_HOME = '/partner';

/**
 * Route meta used for access checks:
 * - `partner: true` marks views of the sales partner area. Partner logins can only open these views,
 *   other users cannot open them.
 * - `guiAccess` lists navigation keys; at least one of them must be granted to the user.
 */
export type AccessRouteMeta = {
    partner?: boolean;
    guiAccess?: NavigationDrawerAccess[];
};

/** `answerPage: true` marks answer pages opened from WhatsApp / mail links (no global info dialogs like "Verfügbarkeiten aktualisieren") */
/** `portal` marks the Lehrkraft-Bereich ('teacher', /my-teaching) and the Bewerbungs-Bereich ('applicant', /my-application) */
type RouteMeta = AccessRouteMeta & {
    forAll?: boolean; guestOnly?: boolean; hideNavbar?: boolean; answerPage?: boolean; portal?: 'teacher' | 'applicant'; portalTab?: string;
};

/** Public pages without the app shell that partner logins may open too (legal pages, 404 and token pages without navbar) */
const PARTNER_PUBLIC_PATHS = ['/imprint', '/compliance'];

function isPublicForPartners(to: Route, meta: RouteMeta): boolean {
    if (!meta.forAll) {
        return false;
    }
    return !!meta.hideNavbar || PARTNER_PUBLIC_PATHS.includes(to.path) || to.matched.some((record) => record.path === '*');
}

/**
 * Checks if the logged in user may open a route.
 * @returns true if access is granted, otherwise the path to redirect to
 */
export function resolveRouteAccess(to: Route): true | string {
    const meta = (to.meta || {}) as RouteMeta;
    if (meta.guestOnly) {
        return true;
    }
    const isPartner = store.state.userType === LoginTypes.PARTNER;
    if (isPartner && isPublicForPartners(to, meta)) {
        return true;
    }
    // Lehrkraft-/Bewerbungs-Bereich: teacher-only and applicant-only logins only open their area, office logins without
    // the teacher role not the Lehrkraft-Bereich (redirects are silent, see isSilentRedirect)
    if (!isPartner) {
        const portal = resolvePortalRoute(portalMode(), to.path, meta, hasTeacherPortal());
        if (portal !== true) {
            return to.path === portal ? true : portal;
        }
        if (meta.portal) {
            // Tabs with a menu key (e.g. „Abrechnung“ → PAYOFF, see portalNav.ts) only with that key, otherwise „Heute“
            const granted = NavigationDrawerAccess.getEffectiveAccess((store.state.guiAccess || []) as NavigationDrawerAccess[]);
            if (meta.portal === 'teacher' && !portalTabAllowed(meta.portalTab, granted) && to.path !== TEACHER_PORTAL_HOME) {
                return TEACHER_PORTAL_HOME;
            }
            return true;
        }
    }
    if (!isPartner && meta.forAll && !meta.partner) {
        return true;
    }
    // Partners only open the partner area (forAll pages with the app shell, e.g. /substitution-inquiries, are not for them)
    if (isPartner && !meta.partner) {
        return to.path === PARTNER_HOME ? true : PARTNER_HOME;
    }
    // Employees may open the partner area read-only ("view as agency") if the menu item and read access to the partners are granted
    if (!isPartner && meta.partner && (!(meta.guiAccess && meta.guiAccess.length) || !NavigationDrawerAccess.canViewAsAgency())) {
        return to.path === '/' ? true : '/';
    }
    if (meta.guiAccess && meta.guiAccess.length) {
        const granted = NavigationDrawerAccess.getEffectiveAccess((store.state.guiAccess || []) as NavigationDrawerAccess[]);
        if (!meta.guiAccess.some((key) => granted.includes(key))) {
            const fallback = isPartner ? PARTNER_HOME : '/';
            return to.path === fallback ? true : fallback;
        }
    }
    return true;
}
